import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import CustomSelect from '../../ui/CustomSelect';
import { parseRupiah } from '../../hris/recruitment/utils/formatRupiah';

interface GeneralTermsProps {
  formData: any;
  handleChange: (field: string, value: any) => void;
  errors?: Record<string, string>;
}

export default function GeneralTerms({ formData, handleChange, errors = {} }: GeneralTermsProps) {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());

  const toggleSection = (section: string) => {
    setOpenSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  // Format currency untuk input field (menampilkan format bahkan untuk 0)
  const formatCurrencyInput = (value: any): string => {
    if (!value && value !== 0) return '';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d]/g, '')) : value;
    if (isNaN(numValue)) return '';
    return new Intl.NumberFormat('id-ID').format(numValue);
  };

  const handleCurrencyChange = (field: string, value: string) => {
    const rawValue = parseRupiah(value);
    if (rawValue === '') {
      handleChange(field, 0);
      return;
    }
    const numValue = parseFloat(rawValue);
    handleChange(field, isNaN(numValue) ? 0 : numValue);
  };

  return (
    <div className="bg-white border rounded-lg">
      <div className="p-4 border-b">
        <h3 className="text-lg font-semibold">Ketentuan Umum & Asumsi Kredit</h3>
      </div>
      <div className="divide-y">
        {/* 1. Asumsi Pembiayaan */}
        <div>
          <button
            type="button"
            onClick={() => toggleSection('financing')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <span className="font-medium">1. Asumsi Pembiayaan</span>
            {openSections.has('financing') ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>
          {openSections.has('financing') && (
            <div className="p-4 pt-0 space-y-4 animate-fadeIn">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Metode Angsuran <span className="text-red-500">*</span>
                  </label>
                  <CustomSelect
                    value={formData.repayment_method}
                    onChange={v => handleChange('repayment_method', v)}
                    placeholder="Pilih metode"
                    width="w-full"
                    error={!!errors.repayment_method}
                    options={[
                      { value: 'flat', label: 'Flat' },
                      { value: 'effective', label: 'Efektif' },
                      { value: 'annuity', label: 'Anuitas' },
                    ]}
                  />
                  {errors.repayment_method && (
                    <p className="text-sm text-red-600 mt-1">{errors.repayment_method}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Repayment Capacity (%) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.repayment_capacity_percentage}
                    onChange={e =>
                      handleChange('repayment_capacity_percentage', parseFloat(e.target.value))
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
                      errors.repayment_capacity_percentage ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {errors.repayment_capacity_percentage && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.repayment_capacity_percentage}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Jenis Bunga per Tahun <span className="text-red-500">*</span>
                  </label>
                  <CustomSelect
                    value={formData.interest_type}
                    onChange={v => handleChange('interest_type', v)}
                    placeholder="Pilih jenis"
                    width="w-full"
                    error={!!errors.interest_type}
                    options={[
                      { value: 'flat', label: 'Flat' },
                      { value: 'effective', label: 'Efektif' },
                    ]}
                  />
                  {errors.interest_type && (
                    <p className="text-sm text-red-600 mt-1">{errors.interest_type}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Suku Bunga per Tahun (%) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.interest_rate}
                    onChange={e => handleChange('interest_rate', parseFloat(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
                      errors.interest_rate ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {errors.interest_rate && (
                    <p className="text-sm text-red-600 mt-1">{errors.interest_rate}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 2. Biaya & Komponen Tambahan */}
        <div>
          <button
            type="button"
            onClick={() => toggleSection('fees')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <span className="font-medium">2. Biaya & Komponen Tambahan</span>
            {openSections.has('fees') ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>
          {openSections.has('fees') && (
            <div className="p-4 pt-0 space-y-4 animate-fadeIn">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Biaya Admin (%) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.admin_fee_percentage}
                    onChange={e => handleChange('admin_fee_percentage', parseFloat(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
                      errors.admin_fee_percentage ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {errors.admin_fee_percentage && (
                    <p className="text-sm text-red-600 mt-1">{errors.admin_fee_percentage}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Provisi (%) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.provision_percentage}
                    onChange={e => handleChange('provision_percentage', parseFloat(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
                      errors.provision_percentage ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {errors.provision_percentage && (
                    <p className="text-sm text-red-600 mt-1">{errors.provision_percentage}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Biaya Materai (Rp)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      Rp
                    </span>
                    <input
                      type="text"
                      value={formatCurrencyInput(formData.stamp_duty_fee)}
                      onChange={e => handleCurrencyChange('stamp_duty_fee', e.target.value)}
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
                        errors.stamp_duty_fee ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="0"
                    />
                  </div>
                  {errors.stamp_duty_fee && (
                    <p className="text-sm text-red-600 mt-1">{errors.stamp_duty_fee}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Biaya Notaris (Rp)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      Rp
                    </span>
                    <input
                      type="text"
                      value={formatCurrencyInput(formData.notary_fee)}
                      onChange={e => handleCurrencyChange('notary_fee', e.target.value)}
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
                        errors.notary_fee ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="0"
                    />
                  </div>
                  {errors.notary_fee && (
                    <p className="text-sm text-red-600 mt-1">{errors.notary_fee}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
